import { useEffect, useState } from 'react'

import type { Conversation } from './conversations-model'
import type { InteractionAggregateRouteRepository } from './interaction-aggregate-repository'

type RouteState =
  | { status: 'idle' | 'loading' | 'error'; conversation: null }
  | { status: 'ready'; conversation: Conversation & { patientId: string } }

/**
 * The selected conversation with its legacy thread route.
 *
 * Derived from the current selection on every render: a route result applies
 * only to the conversation (and tenant) it was resolved for, so the previous
 * selection's thread is never returned for a new one.
 */
export function useInteractionAggregateRoute(
  repository: InteractionAggregateRouteRepository | undefined,
  tenantId: string,
  conversation: Conversation | null
): RouteState {
  const [resolved, setResolved] = useState<{
    tenantId: string
    source: Conversation
    state: RouteState
  } | null>(null)

  useEffect(() => {
    if (
      !conversation ||
      conversation.identityBasis === 'LEGACY_PATIENT_KEY' ||
      !repository
    ) {
      return
    }
    let active = true
    const source = conversation
    void repository
      .getLegacyRoute(tenantId, source.aggregateId)
      .then((route) => {
        if (!active) return
        setResolved({
          tenantId,
          source,
          state:
            route.aggregateId === source.aggregateId
              ? {
                  status: 'ready',
                  conversation: {
                    ...source,
                    patientId: route.legacyPatientId,
                    lastMessageThreadId: route.lastMessageThreadId,
                    lastMessageCallId: route.lastMessageCallId,
                  },
                }
              : { status: 'error', conversation: null },
        })
      })
      .catch(() => {
        if (active) {
          setResolved({
            tenantId,
            source,
            state: { status: 'error', conversation: null },
          })
        }
      })
    return () => {
      active = false
    }
  }, [conversation, repository, tenantId])

  if (!conversation) return { status: 'idle', conversation: null }
  if (conversation.identityBasis === 'LEGACY_PATIENT_KEY') {
    return { status: 'ready', conversation }
  }
  if (!repository) return { status: 'error', conversation: null }
  if (resolved?.source !== conversation || resolved.tenantId !== tenantId) {
    return { status: 'loading', conversation: null }
  }
  return resolved.state
}
